/**
 * Prompts for the AI Event Success Predictor (Phase 2H).
 *
 * The forecast itself (attendance, sell-through, sellout probability, channel
 * mix) is computed deterministically by the `ai-event-predictor` statistics
 * endpoint. The model only narrates and advises on those numbers.
 */

import type { ForecastStats } from '../types';

export const FORECAST_LIMITS = {
  maxOutputTokens: 500,
  maxSummaryChars: 600,
  maxTipChars: 200,
  maxTips: 3,
  maxTitleChars: 200,
} as const;

export interface ForecastPromptInput {
  stats: ForecastStats;
  language: string;
  title?: string;
  location?: string;
  price?: string;
  capacity?: number | null;
}

export function forecastSystemPrompt(input: ForecastPromptInput): string {
  return [
    'You are an event success advisor for a ticketing platform.',
    'You are given a pre-computed forecast. Treat every number as ground truth.',
    'NEVER invent, recompute or contradict a number: only reference figures present in the data.',
    'Never mention or identify individual competing events, organizers or venues.',
    `Write in the language with code "${input.language}".`,
    'Respond with a JSON object exactly of the form:',
    '{"summary": string, "tips": string[]}',
    `"summary" is at most two sentences. "tips" holds up to ${FORECAST_LIMITS.maxTips} short, concrete, actionable items the organizer can do before publishing.`,
    'Do not wrap the JSON in code fences and do not add any other keys.',
  ].join(' ');
}

export function forecastUserPrompt(input: ForecastPromptInput): string {
  const { stats } = input;
  const lines = [
    `Event title: ${input.title?.slice(0, FORECAST_LIMITS.maxTitleChars) || 'Untitled'}.`,
    `Location: ${input.location || 'not specified'}.`,
    `Ticket price: ${input.price || 'not specified'}.`,
    `Planned capacity: ${input.capacity ?? 'not specified'}.`,
    `Comparable events: ${stats.similarEvents} (${stats.eventsWithSales} with recorded sales).`,
    `Average sell-through of comparable events: ${stats.avgSellThrough}%.`,
    `Predicted sell-through for this event: ${stats.predictedSellThrough}%.`,
    `Expected attendance: ${stats.expectedAttendance.mid} (range ${stats.expectedAttendance.low}–${stats.expectedAttendance.high}).`,
    `Sellout probability: ${stats.selloutProbability}.`,
    `Recommended marketing channels: ${stats.recommendedChannels.join(', ') || 'none'}.`,
    `Statistical confidence: ${stats.confidence}.`,
  ];

  if (stats.insufficientSample) {
    lines.push(
      'IMPORTANT: the comparable sample is too small to be reliable. Say so plainly and keep the advice general.',
    );
  }

  return lines.join('\n');
}
